import { BACKEND_API_URL } from '../apiConfig';

/**
 * Check if Upstox API is authenticated
 */
export const checkUpstoxStatus = async () => {
  try {
    const response = await fetch(`${BACKEND_API_URL}/api/upstox/status`);
    const data = await response.json();
    return {
      authenticated: data.authenticated || false,
      hasCredentials: data.hasCredentials || false,
      dbAvailable: data.dbAvailable !== undefined ? data.dbAvailable : true,
      message: data.message || 'Unknown status'
    };
  } catch (error) {
    console.error('Error checking Upstox status:', error);
    return {
      authenticated: false,
      hasCredentials: false,
      dbAvailable: false,
      message: 'Error checking authentication status'
    };
  }
};

/**
 * Refresh token from backend .env file
 */
export const refreshUpstoxToken = async () => {
  try {
    const response = await fetch(`${BACKEND_API_URL}/api/upstox/refresh-token`);
    const data = await response.json();
    return {
      authenticated: data.authenticated || false,
      hasCredentials: data.hasCredentials || false,
      dbAvailable: data.dbAvailable !== undefined ? data.dbAvailable : true,
      message: data.message || 'Unknown status'
    };
  } catch (error) {
    console.error('Error refreshing Upstox token:', error);
    return {
      authenticated: false,
      hasCredentials: false,
      dbAvailable: false,
      message: 'Error refreshing token'
    };
  }
};

/**
 * Get Upstox authorization URL
 */
export const getUpstoxAuthUrl = async () => {
  try {
    const response = await fetch(`${BACKEND_API_URL}/api/upstox/auth-url`);
    const data = await response.json();
    return data.loginUrl;
  } catch (error) {
    console.error('Error getting Upstox auth URL:', error);
    return null;
  }
};

/**
 * Initiate Upstox token generation by opening the authorization URL
 * Returns a promise that resolves when the authorization window is closed
 */
export const initiateUpstoxTokenGeneration = async () => {
  try {
    const authUrl = await getUpstoxAuthUrl();
    
    if (!authUrl) {
      throw new Error('Failed to get authorization URL');
    }

    // Open authorization URL in a new window
    const authWindow = window.open(authUrl, 'upstox_auth', 'width=600,height=700,status=no,menubar=no');
    
    if (!authWindow) {
      throw new Error('Failed to open authorization window. Please check if pop-ups are blocked.');
    }

    // Return a promise that resolves when authorization is complete
    return new Promise((resolve, reject) => {
      let messageHandler = null;
      const maxRetries = 30; // Check for up to 30 seconds
      
      // Listen for message from auth callback
      messageHandler = (event) => {
        if (event.data && event.data.type === 'UPSTOX_AUTH_SUCCESS') {
          window.removeEventListener('message', messageHandler);
          clearInterval(checkWindowInterval);
          
          // Wait a moment for the window to close and backend to update
          setTimeout(() => {
            checkAuthWithRetry(0, maxRetries)
              .then(status => {
                if (status.authenticated) {
                  resolve({ success: true, message: 'Token generated successfully' });
                } else {
                  reject(new Error('Authentication completed but token was not saved'));
                }
              })
              .catch(reject);
          }, 500);
        }
      };
      
      window.addEventListener('message', messageHandler);
      
      // Also check if the window was closed by the user (fallback)
      const checkWindowInterval = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(checkWindowInterval);
          window.removeEventListener('message', messageHandler);
          
          // Check if authentication was successful with retries
          checkAuthWithRetry(0, maxRetries)
            .then(status => {
              if (status.authenticated) {
                resolve({ success: true, message: 'Token generated successfully' });
              } else {
                reject(new Error('Authentication window closed without completing authorization'));
              }
            })
            .catch(reject);
        }
      }, 500);

      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(checkWindowInterval);
        window.removeEventListener('message', messageHandler);
        if (!authWindow.closed) {
          authWindow.close();
        }
        reject(new Error('Authorization timeout'));
      }, 5 * 60 * 1000);
    });
  } catch (error) {
    console.error('Error initiating token generation:', error);
    throw error;
  }
};

/**
 * Check auth status with retry logic
 */
async function checkAuthWithRetry(attempt = 0, maxRetries = 10) {
  try {
    // Use refresh-token endpoint to reload .env file on backend
    const status = await refreshUpstoxToken();
    
    if (status.authenticated) {
      return status;
    }
    
    // If not authenticated and we have retries left, wait and retry
    if (attempt < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
      return checkAuthWithRetry(attempt + 1, maxRetries);
    }
    
    return status;
  } catch (error) {
    if (attempt < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return checkAuthWithRetry(attempt + 1, maxRetries);
    }
    throw error;
  }
}
